// The ONE way a WGSL module is assembled.
//
// A WebGPU pipeline takes ONE module that carries the whole program: the
// uniform block, the texture bindings, the varying struct, the vertex input
// struct, a `@vertex` entry and a `@fragment` entry. GLSL splits the same
// material over two sources and repeats the contract in each, so every twin
// of `./` writes a BODY — functions, module consts and `@fragment fn fs` —
// and nothing else.
//
// This file joins the parts, and it is the only place that joins them. Four
// agents ported the world passes in parallel and each one assembled its
// module its own way: one built the header from the pass's own uniform list
// while the packer read the whole block, and none of the world modules
// carried a vertex entry at all, so no world pipeline could be created. One
// function removes both faults for every pass at once.
//
// The order is fixed so every module reads the same way and a compile error
// names the same line in each: the header, the vertex input struct, the
// vertex entry, then the body. WGSL itself reads module-scope declarations
// in any order.

import type { AttribSpec } from '../../gfx/types';
import { CORNER_ATTRIB } from '../../gl/corner';
import type { UniformSpec } from '../prelude';
import { wgslVertexInput } from './attribs';
import { worldHeaderWgsl } from './prelude';

/** The parts of one module, as every pass of the engine hands them over. */
export interface WgslModuleParts {
  /**
   * The WHOLE uniform block in order.
   *
   * The engine hands the block `ProgramSource.uniforms` carries — the
   * driver's three uniforms, the shared contract and the pass's own — so the
   * shader's `struct Uniforms` and the packer's byte offsets are computed
   * from ONE list. The roster hands the pass's own list instead, because the
   * parity suite holds the twin against the GLSL of that pass alone.
   */
  readonly uniforms: readonly UniformSpec[];
  /** The body: functions, module consts and `@fragment fn fs`. */
  readonly body: string;
  /**
   * The `@vertex fn vs` entry, WITHOUT its input struct.
   *
   * The default is no vertex entry at all, which is the fragment-half module
   * the parity suite reads. A pass that draws hands the shared world or
   * screen entry, or one of its own.
   */
  readonly vertex?: string;
  /** The attributes. The default is the corner alone. */
  readonly attribs?: readonly AttribSpec[];
  /** The varying struct, where the pass does not read the shared one. */
  readonly varyings?: string;
}

/** One whole WGSL module: the header, the vertex half and the body. */
export function wgslModule(parts: WgslModuleParts): string {
  return [
    worldHeaderWgsl(parts.uniforms, { varyings: parts.varyings }),
    wgslVertexInput(parts.attribs ?? [CORNER_ATTRIB]),
    parts.vertex ?? '',
    parts.body,
  ].join('\n');
}
